import React, { useEffect, useState } from "react";

import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { LiquidityGraph } from "components/Benchmark/Liquidity-graph/LiquidityGraph";
import { CorrelationMatrix } from "components/Investor-portfolio/Correlation-matrix/CorrelationMatrix";
import { useActionHook, useDevice } from "hooks";
import { useCorrelationMatrixSettingsSelector, usePortfolioConstructionActions } from "store";
import { IPortfolioConstructionCorrelationData } from "store/types";

import { CorrelationMatrixEmptyState } from "./CorrelationMatrixEmptyState";
import { FullscreenModal } from "./FullscreenModal";

type FullscreenContentType = "liquidity" | "correlation" | null;

export const Portfolio: React.FC = () => {
  const settings = useCorrelationMatrixSettingsSelector();
  const { getCorrelationDataAction } = usePortfolioConstructionActions();
  const [correlationData, setCorrelationData] = useState<IPortfolioConstructionCorrelationData | null>(null);
  const [fullscreenContent, setFullscreenContent] = useState<FullscreenContentType>(null);
  const { isMobile } = useDevice();

  const { action: getCorrelationData, loading: correlationLoading } = useActionHook(getCorrelationDataAction);

  useEffect(() => {
    getCorrelationData({ ...settings }).then((data) => {
      if (data) {
        setCorrelationData(data);
      }
    });
  }, [settings]);

  const handleLiquidityFullscreen = () => {
    setFullscreenContent("liquidity");
  };

  const handleCorrelationFullscreen = () => {
    setFullscreenContent("correlation");
  };

  const handleCloseFullscreen = () => {
    setFullscreenContent(null);
  };

  return (
    <Stack direction={isMobile ? "column" : "row"} gap="20px" sx={{ width: "100%" }}>
      {/* Liquidity Profile Card */}
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          bgcolor: "white",
          borderRadius: "8px",
          boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)",
          p: "16px",
          gap: "16px",
          maxHeight: "401px",
          minHeight: "401px"
        }}
      >
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071f35" }}>Liquidity Profile</Typography>
          <IconButton
            onClick={handleLiquidityFullscreen}
            sx={{
              width: "24px",
              height: "24px",
              padding: "4px",
              borderRadius: "32px"
            }}
          >
            <FullscreenIcon sx={{ fontSize: "16px", color: "#071f35" }} />
          </IconButton>
        </Stack>

        {/* Content */}
        <Stack sx={{ flex: 1 }}>
          <LiquidityGraph isFreemium />
        </Stack>
      </Stack>

      {/* Correlation Matrix Card */}
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          bgcolor: "white",
          borderRadius: "8px",
          boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)",
          p: "16px",
          gap: "16px",
          maxHeight: "401px"
        }}
      >
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071f35" }}>Correlation Matrix</Typography>
          <IconButton
            onClick={handleCorrelationFullscreen}
            sx={{
              width: "24px",
              height: "24px",
              padding: "4px",
              borderRadius: "32px"
            }}
          >
            <FullscreenIcon sx={{ fontSize: "16px", color: "#071f35" }} />
          </IconButton>
        </Stack>

        {/* Content */}
        <Stack sx={{ flex: 1 }}>
          {correlationLoading && (
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "center",
                py: "40px",
                flex: 1,
                height: "100%",
                minHeight: "329px"
              }}
            >
              <CircularProgress size={40} sx={{ color: "#071f35" }} />
            </Stack>
          )}
          {!correlationLoading && correlationData && <CorrelationMatrix data={correlationData} isFreemium />}
          {!correlationLoading && !correlationData && <CorrelationMatrixEmptyState />}
        </Stack>
      </Stack>

      {/* Fullscreen Modals */}
      <FullscreenModal
        open={fullscreenContent === "liquidity"}
        title="Liquidity profile"
        onClose={handleCloseFullscreen}
      >
        <LiquidityGraph isFreemium />
      </FullscreenModal>

      <FullscreenModal
        open={fullscreenContent === "correlation"}
        title="Correlation matrix"
        onClose={handleCloseFullscreen}
      >
        {correlationLoading && (
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
              py: "40px",
              flex: 1,
              height: "100%",
              minHeight: "329px"
            }}
          >
            <CircularProgress size={40} sx={{ color: "#071f35" }} />
          </Stack>
        )}
        {!correlationLoading && correlationData && <CorrelationMatrix data={correlationData} />}
        {!correlationLoading && !correlationData && <CorrelationMatrixEmptyState />}
      </FullscreenModal>
    </Stack>
  );
};
